// ============================================================
// Leitor mínimo de .xlsx (OOXML) — sem biblioteca de planilha.
// ============================================================
// Simétrico ao `xlsx-export.ts`: lá montamos as partes OOXML e zipamos com
// JSZip; aqui deszipamos e lemos de volta. Motivo de não usar uma lib: `jszip`
// já está no bundle (o export depende dela) e adicionar `xlsx`/`exceljs`
// custaria ~1MB + um `npm install` que quebra no chromedriver neste projeto.
//
// Devolve SÓ TEXTO (string[][]) — a planilha de autores é toda textual. Não
// interpreta datas seriais nem formatação; números viram o texto do `<v>`.
//
// Robustez exigida por planilhas do mundo real (validado contra uma exportada
// com ~1M de linhas fantasma):
//   • Células ESPARSAS: a posição vem do atributo `r` ("C7" → coluna 2), NUNCA
//     da ordem dos `<c>` — uma linha pode pular colunas.
//   • Linhas/células FANTASMA: o Excel emite `<row>` vazio até o fim da planilha
//     (1.048.576). Linha sem nenhum valor não-vazio é DESCARTADA.
//   • Tipos de célula: `s` (sharedStrings), `inlineStr` (<is><t>), `str`
//     (resultado de fórmula), `b` (booleano) e sem `t` (número).
//   • Rich text: um `<si>`/`<is>` pode ter vários `<t>` (trechos formatados) —
//     concatena todos.

import * as JSZipNS from 'jszip';

// Interop CJS/ESM — mesma razão documentada em `xlsx-export.ts`.
const JSZip: any = (JSZipNS as any).default || JSZipNS;

// Teto de segurança: acima disso a planilha é grande demais pra tabela da tela
// (e quase certamente é engano do usuário). Conta só linhas COM conteúdo.
export const XLSX_MAX_ROWS = 5000;

export class XlsxReadError extends Error {
  constructor(public readonly code: 'not-a-xlsx' | 'no-sheet' | 'too-many-rows' | 'no-email-column' | 'no-title-column'
                                    | 'no-id-column' | 'no-reviewer-column', message?: string) {
    super(message || code);
    // Necessário no target ES5 do projeto pra `instanceof` funcionar.
    Object.setPrototypeOf(this, XlsxReadError.prototype);
  }
}

// "C7" → 2 (índice 0-based da coluna). Ignora a parte numérica da linha.
function colIndexFromRef(ref: string): number {
  let n = 0;
  for (let i = 0; i < ref.length; i++) {
    const code = ref.charCodeAt(i);
    if (code < 65 || code > 90) { break; }       // chegou nos dígitos da linha
    n = n * 26 + (code - 64);
  }
  return n - 1;
}

// Concatena todos os `<t>` de um nó (`<si>` ou `<is>`), cobrindo rich text.
function textOf(node: Element): string {
  const ts = node.getElementsByTagNameNS('*', 't');
  let out = '';
  for (let i = 0; i < ts.length; i++) { out += ts[i].textContent || ''; }
  return out;
}

function parseXml(xml: string, what: string): Document {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) {
    throw new XlsxReadError('not-a-xlsx', `XML inválido em ${what}`);
  }
  return doc;
}

/**
 * Lê a PRIMEIRA planilha de um .xlsx e devolve a matriz de textos já aparada.
 *
 * A primeira aba é sempre a usada: o nome da aba não é confiável (planilhas
 * reais chegam com nome herdado de outro trabalho, ex.: "FIB16_TodasPropostas_").
 */
export async function readXlsxSheet(file: File | Blob): Promise<string[][]> {
  let zip: any;
  try {
    zip = await JSZip.loadAsync(file);
  } catch {
    // Não é ZIP → .xls antigo, .csv renomeado ou arquivo corrompido.
    throw new XlsxReadError('not-a-xlsx');
  }

  const sheetFile = zip.file('xl/worksheets/sheet1.xml');
  if (!sheetFile) { throw new XlsxReadError('no-sheet'); }

  // sharedStrings é OPCIONAL: planilhas 100% inlineStr (Google Sheets, nosso
  // próprio export) não têm essa parte.
  const shared: string[] = [];
  const sharedFile = zip.file('xl/sharedStrings.xml');
  if (sharedFile) {
    const doc = parseXml(await sharedFile.async('string'), 'sharedStrings.xml');
    const sis = doc.getElementsByTagNameNS('*', 'si');
    for (let i = 0; i < sis.length; i++) { shared.push(textOf(sis[i])); }
  }

  const doc = parseXml(await sheetFile.async('string'), 'sheet1.xml');
  const rowNodes = doc.getElementsByTagNameNS('*', 'row');
  const rows: string[][] = [];

  for (let i = 0; i < rowNodes.length; i++) {
    const cellNodes = rowNodes[i].getElementsByTagNameNS('*', 'c');
    const values: string[] = [];
    let hasContent = false;

    for (let j = 0; j < cellNodes.length; j++) {
      const c = cellNodes[j];
      const ref = c.getAttribute('r');
      // Sem `r` (raro, mas o schema permite) cai na ordem sequencial.
      const col = ref ? colIndexFromRef(ref) : j;
      const type = c.getAttribute('t');

      let value = '';
      if (type === 's') {
        const v = c.getElementsByTagNameNS('*', 'v')[0];
        const idx = v ? parseInt(v.textContent, 10) : NaN;
        value = isNaN(idx) ? '' : (shared[idx] || '');
      } else if (type === 'inlineStr') {
        const is = c.getElementsByTagNameNS('*', 'is')[0];
        value = is ? textOf(is) : '';
      } else {
        // `str` (fórmula), `b` (booleano) e número: todos no `<v>`.
        const v = c.getElementsByTagNameNS('*', 'v')[0];
        value = v ? (v.textContent || '') : '';
      }

      // Trim já aqui: as planilhas chegam com espaço sobrando ("Karine  ").
      value = value.trim();
      // Célula vazia não ocupa posição: o Excel às vezes grava uma célula
      // formatada-mas-vazia muito à direita (visto: `XDJ6`, coluna 16.337) e
      // preencher o vão até ela alocaria 16 mil strings vazias por linha.
      // Como a leitura é por índice (`cells[i] || ''`), ausente == vazia.
      if (!value) { continue; }
      hasContent = true;

      // Preenche o buraco até a célula com conteúdo (linhas esparsas).
      while (values.length < col) { values.push(''); }
      values[col] = value;
    }

    // Descarta as ~1M linhas fantasma que o Excel emite até o fim da planilha.
    if (!hasContent) { continue; }

    if (rows.length >= XLSX_MAX_ROWS) { throw new XlsxReadError('too-many-rows'); }
    rows.push(values);
  }

  return rows;
}
